// src/pages/ROSearchPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import ROStatusTimeline from "../components/ro/ROStatusTimeline";
import {
  formatVehicleLabel,
  getLatestNotePreview,
  getRoCorePills,
  getRoRowViewModel,
  getRoSecondaryIndicators,
  shouldHighlightRoNumber,
} from "../utils/roFormat";
import { safeString } from "../utils/roModel";
import { TRANSPORT_TYPES, WORK_MIX } from "../constants/roStatus";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").trim();

function apiUrl(path) {
  const p = String(path || "").replace(/^\/+/, "");
  if (!API_BASE) return `/${p}`;
  return `${API_BASE.replace(/\/+$/, "")}/${p}`;
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(iso) {
  const d = safeDate(iso);
  if (!d) return "—";
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function normalize(text) {
  return String(text ?? "").trim().toLowerCase();
}

function uniq(arr) {
  return Array.from(new Set(arr));
}

function matchesDateRange(iso, fromDate, toDate) {
  const d = safeDate(iso);
  if (!d) return false;

  if (fromDate) {
    const from = new Date(`${fromDate}T00:00:00`);
    if (d < from) return false;
  }

  if (toDate) {
    const to = new Date(`${toDate}T23:59:59`);
    if (d > to) return false;
  }

  return true;
}

function normalizeVinLast8(v) {
  return safeString(v).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(-8);
}

function firstNonBlank(...values) {
  for (const value of values) {
    const s = safeString(value).trim();
    if (s) return s;
  }
  return "";
}

function repairRO(ro) {
  const vin = normalizeVinLast8(
    firstNonBlank(
      ro?.vinLast8,
      ro?.vin_last_8,
      ro?.vin_last8,
      ro?.vinLast,
      ro?.vin
    )
  );

  return {
    ...(ro && typeof ro === "object" ? ro : {}),
    vin,
    vinLast8: vin,
    vin_last_8: vin,
    vin_last8: vin,
    vinLast: vin,
  };
}

function getVinDisplay(ro) {
  return normalizeVinLast8(
    firstNonBlank(
      ro?.vinLast8,
      ro?.vin_last_8,
      ro?.vin_last8,
      ro?.vinLast,
      ro?.vin
    )
  );
}

function toUpperTrim(v) {
  return safeString(v).trim().toUpperCase();
}

function toDateTimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function basePillStyle() {
  return {
    fontSize: "0.68rem",
    borderRadius: "999px",
    padding: "0.18rem 0.5rem",
    fontWeight: 800,
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.1,
  };
}

function tagPillStyle() {
  const base = basePillStyle();
  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function corePillStyle(kind) {
  const base = basePillStyle();

  if (kind === "transport") {
    return {
      ...base,
      background: "#f8fafc",
      borderColor: "#cbd5e1",
      color: "#0f172a",
    };
  }

  if (kind === "workMix") {
    return {
      ...base,
      background: "#fff7ed",
      borderColor: "#fdba74",
      color: "#9a3412",
    };
  }

  return {
    ...base,
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  };
}

function secondaryIndicatorStyle(type) {
  const base = {
    fontSize: "0.68rem",
    borderRadius: "999px",
    padding: "0.16rem 0.45rem",
    fontWeight: 800,
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.1,
    border: "1px solid transparent",
  };

  if (type === "attention") {
    return {
      ...base,
      background: "#fff7ed",
      color: "#9a3412",
      borderColor: "#fdba74",
    };
  }

  if (type === "risk") {
    return {
      ...base,
      background: "#fef2f2",
      color: "#b91c1c",
      borderColor: "#fecaca",
      minWidth: 28,
      justifyContent: "center",
    };
  }

  if (type === "aging") {
    return {
      ...base,
      background: "#faf5ff",
      color: "#7c3aed",
      borderColor: "#ddd6fe",
      minWidth: 28,
      justifyContent: "center",
    };
  }

  if (type === "photos") {
    return {
      ...base,
      background: "#f8fafc",
      color: "#0f172a",
      borderColor: "#cbd5e1",
    };
  }

  return {
    ...base,
    background: "#f8fafc",
    color: "#334155",
    borderColor: "#e2e8f0",
  };
}

function sectionCardStyle() {
  return {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 14,
    padding: 11,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
  };
}

function emptyStateStyle() {
  return {
    padding: 10,
    border: "1px dashed #cbd5e1",
    borderRadius: 12,
    color: "rgba(15,23,42,0.55)",
    background: "#f8fafc",
    fontSize: 11.5,
  };
}

function fieldStyle() {
  return {
    height: 34,
    padding: "0 10px",
    borderRadius: 9,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "#ffffff",
    color: "rgba(15,23,42,0.90)",
    fontSize: 12.2,
    fontWeight: 650,
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };
}

function formatOperationalLabel(value) {
  const raw = safeString(value).trim();
  if (!raw) return "—";

  return raw
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function statusLabel(ro) {
  return formatOperationalLabel(ro?.status);
}

function lifecycleLabel(ro) {
  return formatOperationalLabel(ro?.lifecycle);
}

function asFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getEventList(ro) {
  const lists = [
    ro?.events,
    ro?.history,
    ro?.timeline,
    ro?.auditTrail,
    ro?.activity,
    ro?.activityLog,
  ];

  return lists.flatMap((list) => (Array.isArray(list) ? list : []));
}

function getNumberFromEvent(event, keys = []) {
  const meta = event?.meta && typeof event.meta === "object" ? event.meta : {};

  for (const key of keys) {
    const direct = asFiniteNumber(event?.[key]);
    if (direct != null && direct > 0) return direct;

    const fromMeta = asFiniteNumber(meta?.[key]);
    if (fromMeta != null && fromMeta > 0) return fromMeta;
  }

  const changes = Array.isArray(meta?.changes)
    ? meta.changes
    : Array.isArray(meta?.changedFields)
    ? meta.changedFields
    : [];

  for (const change of changes) {
    const field = safeString(
      change?.field || change?.key || change?.name || ""
    )
      .trim()
      .toLowerCase();

    if (
      field === "flaggedhours" ||
      field === "flagged_hours" ||
      field === "hoursflagged" ||
      field === "hours_flagged" ||
      field === "completedhours" ||
      field === "completed_hours" ||
      field === "hourscompleted" ||
      field === "hours_completed"
    ) {
      const next = asFiniteNumber(
        change?.to ?? change?.next ?? change?.newValue ?? change?.value
      );
      if (next != null && next > 0) return next;
    }
  }

  return null;
}

function getFlaggedHoursValue(ro) {
  const directValues = [
    ro?.flaggedHours,
    ro?.flagged_hours,
    ro?.hoursFlagged,
    ro?.hours_flagged,
    ro?.completedHours,
    ro?.completed_hours,
    ro?.hoursCompleted,
    ro?.hours_completed,
    ro?.technicianFlaggedHours,
    ro?.technician_flagged_hours,
  ];

  for (const value of directValues) {
    const n = asFiniteNumber(value);
    if (n != null && n > 0) return n;
  }

  const events = getEventList(ro).slice().reverse();

  for (const event of events) {
    const n = getNumberFromEvent(event, [
      "flaggedHours",
      "flagged_hours",
      "hoursFlagged",
      "hours_flagged",
      "completedHours",
      "completed_hours",
      "hoursCompleted",
      "hours_completed",
      "technicianFlaggedHours",
      "technician_flagged_hours",
    ]);

    if (n != null && n > 0) return n;
  }

  return null;
}

function formatFlaggedHours(ro) {
  const n = getFlaggedHoursValue(ro);
  if (n == null) return "—";
  return n.toFixed(1);
}

function modalOverlayStyle() {
  return {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    zIndex: 9999,
  };
}

function modalCardStyle(width = 920) {
  return {
    width,
    maxWidth: "100%",
    maxHeight: "88vh",
    overflow: "hidden",
    background: "#ffffff",
    border: "1px solid #dbe3ee",
    borderRadius: 18,
    boxShadow: "0 26px 60px rgba(15,23,42,0.22)",
    display: "grid",
    gridTemplateRows: "auto minmax(0,1fr) auto",
  };
}

function modalHeaderStyle() {
  return {
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#ffffff",
  };
}

function modalBodyStyle() {
  return {
    padding: 16,
    overflow: "auto",
    display: "grid",
    gap: 14,
  };
}

function modalFooterStyle() {
  return {
    padding: "14px 16px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    background: "#ffffff",
  };
}

function btnStyle(kind = "default", disabled = false) {
  let border = "rgba(15,23,42,0.12)";
  let background = "#ffffff";
  let color = "rgba(15,23,42,0.90)";

  if (kind === "primary") {
    border = "#0f172a";
    background = "#0f172a";
    color = "#ffffff";
  } else if (kind === "danger") {
    border = "#fecaca";
    background = "#ffffff";
    color = "#b91c1c";
  }

  if (disabled) {
    border = "#e5e7eb";
    background = "#f9fafb";
    color = "#9ca3af";
  }

  return {
    minHeight: 34,
    padding: "0 12px",
    borderRadius: 9,
    border: `1px solid ${border}`,
    background,
    color,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 850,
    fontSize: 12.2,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  };
}

function detailGridStyle() {
  return {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
  };
}

function detailFieldCardStyle() {
  return {
    border: "1px solid rgba(15,23,42,0.07)",
    borderRadius: 12,
    padding: "7px 9px",
    background: "#ffffff",
    minWidth: 0,
  };
}

function detailLabelStyle() {
  return {
    fontSize: 10.2,
    fontWeight: 850,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.44)",
    marginBottom: 3,
  };
}

function detailValueStyle() {
  return {
    fontSize: 12.2,
    color: "rgba(15,23,42,0.80)",
    fontWeight: 800,
    wordBreak: "break-word",
  };
}

function errorTextStyle() {
  return {
    marginTop: 6,
    fontSize: 12,
    color: "#991b1b",
    fontWeight: 800,
  };
}

function helperTextStyle() {
  return {
    marginTop: 6,
    fontSize: 12,
    color: "#6b7280",
  };
}

function canVoidRole(role) {
  const r = safeString(role).trim().toLowerCase();
  return (
    r === "advisor" ||
    r === "service advisor" ||
    r === "service_advisor" ||
    r === "manager" ||
    r === "admin"
  );
}

const pageShell = {
  padding: 12,
  display: "grid",
  gap: 10,
  maxWidth: 1500,
  margin: "0 auto",
};

const pageHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const pageEyebrow = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
  marginBottom: 3,
};

const pageTitle = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const pageSubtitle = {
  margin: "4px 0 0",
  fontSize: 11.5,
  color: "rgba(15,23,42,0.58)",
  lineHeight: 1.32,
};

const sectionTitleText = {
  fontSize: 10.5,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "rgba(15,23,42,0.72)",
};

const searchButton = {
  height: 34,
  padding: "0 12px",
  borderRadius: 9,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "#ffffff",
  color: "rgba(15,23,42,0.90)",
  cursor: "pointer",
  fontWeight: 850,
  fontSize: 12.2,
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};

const resultCountPill = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: 24,
  padding: "2px 8px",
  borderRadius: 999,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "rgba(248,250,252,0.94)",
  color: "rgba(15,23,42,0.68)",
  fontSize: 10.5,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

function makeEditForm(ro) {
  return {
    roNumber: safeString(ro?.roNumber).trim(),
    tagNumber: safeString(ro?.tagNumber).trim(),
    vinLast8: getVinDisplay(ro),
    customerName: safeString(ro?.customerName).trim(),
    vehicle: safeString(ro?.vehicle).trim(),
    advisorName: safeString(ro?.advisorName).trim(),
    advisorEmploymentId: safeString(ro?.advisorEmploymentId || ro?.advisor_employment_id).trim(),
    promiseTimeISO: toDateTimeLocalValue(ro?.promiseTimeISO),
    workMix: safeString(ro?.workMix).trim() || WORK_MIX.OTHER,
    transportType: safeString(ro?.transportType).trim() || TRANSPORT_TYPES.OTHER,
  };
}

export default function ROSearchPage() {
  const store = useROStore();
  const auth = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateRO = store?.updateRO;
  const deleteRO = store?.deleteRO;

  const currentUser = useMemo(() => store?.currentUser || {}, [store?.currentUser]);

  const fallbackRos = useMemo(() => {
    const a = Array.isArray(store?.repairOrders) ? store.repairOrders : null;
    const b = Array.isArray(store?.ros) ? store.ros : null;
    return (a || b || []).map(repairRO);
  }, [store?.repairOrders, store?.ros]);

  const [liveRos, setLiveRos] = useState([]);
  const [liveRosLoaded, setLiveRosLoaded] = useState(false);

  const ros = useMemo(() => {
    return liveRosLoaded ? liveRos : fallbackRos;
  }, [liveRosLoaded, liveRos, fallbackRos]);

  const refreshRos = useCallback(async () => {
    const token = safeString(auth?.token).trim();
    const storeId = safeString(
      auth?.effectiveStoreId || auth?.user?.storeId || ""
    ).trim();

    if (!token || !storeId) return;

    try {
      const res = await fetch(apiUrl(`/api/ros?_ts=${Date.now()}`), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Store-Id": storeId,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;

      const rows = Array.isArray(data?.ros) ? data.ros.map(repairRO) : [];
      setLiveRos(rows);
      setLiveRosLoaded(true);
    } catch {
      // leave fallback store data in place
    }
  }, [auth?.token, auth?.effectiveStoreId, auth?.user?.storeId]);

  useEffect(() => {
    refreshRos();
  }, [refreshRos]);

  const techs = useMemo(() => {
    const a = Array.isArray(store?.techs) ? store.techs : null;
    const b = Array.isArray(store?.technicians) ? store.technicians : null;
    return a || b || [];
  }, [store?.techs, store?.technicians]);

  const techNameById = useMemo(() => {
    const m = new Map();
    (techs || []).forEach((t) => {
      m.set(String(t?.id), safeString(t?.name) || String(t?.id));
    });
    return m;
  }, [techs]);

  const getTechName = useCallback(
    (techId) => {
      if (!techId) return "—";
      return techNameById.get(String(techId)) || String(techId);
    },
    [techNameById]
  );

  const [query, setQuery] = useState("");
  const [selectedROId, setSelectedROId] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [advisorFilter, setAdvisorFilter] = useState("");
  const [techFilter, setTechFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [modalBanner, setModalBanner] = useState(null);

  const [editForm, setEditForm] = useState({
    roNumber: "",
    tagNumber: "",
    vinLast8: "",
    customerName: "",
    vehicle: "",
    advisorName: "",
    advisorEmploymentId: "",
    promiseTimeISO: "",
    workMix: WORK_MIX.OTHER,
    transportType: TRANSPORT_TYPES.OTHER,
  });

  const [fieldErrors, setFieldErrors] = useState({
    roNumber: "",
    tagNumber: "",
    vinLast8: "",
    customerName: "",
    vehicle: "",
    advisorName: "",
  });

  const hydratedModalRef = useRef("");

  const urlQ = useMemo(() => searchParams.get("q") || "", [searchParams]);
  const navQ = safeString(location?.state?.q || "").trim();

  useEffect(() => {
    const next = String(urlQ || navQ || "").trim();
    if (!next) return;
    setQuery((prev) => (prev === next ? prev : next));
  }, [urlQ, navQ]);

  useEffect(() => {
    const q = normalize(query);
    if (!q) return;

    const qClean = q.replace(/^ro\s*#?/i, "");

    const exactRo =
      ros.find((r) => normalize(r?.roNumber) === q) ||
      ros.find((r) => normalize(r?.roNumber) === qClean) ||
      null;

    if (exactRo?.id) {
      setSelectedROId((prev) => (String(prev) === String(exactRo.id) ? prev : exactRo.id));
      return;
    }

    const exactTag =
      ros.find((r) => normalize(r?.tagNumber) === q) ||
      ros.find((r) => normalize(r?.tagNumber) === qClean) ||
      null;

    if (exactTag?.id) {
      setSelectedROId((prev) => (String(prev) === String(exactTag.id) ? prev : exactTag.id));
      return;
    }

    const exactId = ros.find((r) => normalize(r?.id) === q) || null;
    if (exactId?.id) {
      setSelectedROId((prev) => (String(prev) === String(exactId.id) ? prev : exactId.id));
    }
  }, [query, ros]);

  useEffect(() => {
    const q = String(query || "").trim();

    const handle = window.setTimeout(() => {
      const current = searchParams.get("q") || "";
      if (q === current) return;

      const next = new URLSearchParams(searchParams);
      if (!q) next.delete("q");
      else next.set("q", q);

      setSearchParams(next, { replace: true });
    }, 250);

    return () => window.clearTimeout(handle);
  }, [query, searchParams, setSearchParams]);

  const advisorOptions = useMemo(() => {
    return uniq(
      ros
        .map((r) => safeString(r?.advisorName).trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
    );
  }, [ros]);

  const advisorEditOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const addOption = ({ value, label, name, employmentId }) => {
      const cleanValue = safeString(value).trim();
      const cleanLabel = safeString(label).trim();
      if (!cleanValue || !cleanLabel || seen.has(cleanValue)) return;
      seen.add(cleanValue);
      options.push({
        value: cleanValue,
        label: cleanLabel,
        name: safeString(name || cleanLabel).trim(),
        employmentId: safeString(employmentId).trim(),
      });
    };
    const addLegacyName = (name) => {
      const cleanName = safeString(name).trim();
      if (!cleanName) return;
      addOption({
        value: `name:${cleanName}`,
        label: cleanName,
        name: cleanName,
        employmentId: "",
      });
    };

    const directory = Array.isArray(store?.staffDirectory) ? store.staffDirectory : [];
    directory.forEach((person) => {
      const role = safeString(person?.role).toLowerCase().trim();
      const employmentStatus = safeString(
        person?.employmentStatus ?? person?.employment_status
      ).toUpperCase();
      const isActive =
        person?.active === undefined ? employmentStatus !== "TERMINATED" : !!person.active;
      const employmentId = safeString(person?.id).trim();
      const name = safeString(person?.name).trim();
      if (
        isActive &&
        employmentId &&
        name &&
        (role === "advisor" || role === "service_advisor" || role === "service advisor")
      ) {
        addOption({
          value: `staff:${employmentId}`,
          label: name,
          name,
          employmentId,
        });
      }
    });

    advisorOptions.forEach(addLegacyName);
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [store?.staffDirectory, advisorOptions]);

  const advisorEditSelectValue = editForm.advisorEmploymentId
    ? `staff:${editForm.advisorEmploymentId}`
    : editForm.advisorName
    ? `name:${editForm.advisorName}`
    : "";

  const applyAdvisorEditSelection = useCallback(
    (value) => {
      const selected = advisorEditOptions.find((option) => option.value === value);
      setEditForm((m) => ({
        ...m,
        advisorName: selected?.name || safeString(value).replace(/^name:/, ""),
        advisorEmploymentId: selected?.employmentId || "",
      }));
    },
    [advisorEditOptions]
  );

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      normalize(query) ||
        statusFilter ||
        advisorFilter ||
        techFilter ||
        fromDate ||
        toDate
    );
  }, [query, statusFilter, advisorFilter, techFilter, fromDate, toDate]);

  const results = useMemo(() => {
    if (!hasActiveFilters) return [];

    const q = normalize(query);

    return ros.filter((r) => {
      if (q) {
        const hay = [
          r?.roNumber,
          r?.tagNumber,
          r?.customerName,
          r?.vehicle,
          r?.vin,
          r?.vinLast8,
          r?.vin_last_8,
          r?.advisorName,
          r?.assignedTechName,
          r?.concern,
          r?.notes,
          r?.flaggedHours,
          r?.flagged_hours,
          r?.hoursFlagged,
          r?.hours_flagged,
          r?.completedHours,
          r?.completed_hours,
          r?.partsStatus,
          r?.status,
          r?.lifecycle,
        ].join(" | ");

        if (!normalize(hay).includes(q)) return false;
      }

      if (statusFilter && safeString(r?.status).trim() !== statusFilter) {
        return false;
      }

      if (advisorFilter && safeString(r?.advisorName).trim() !== advisorFilter) {
        return false;
      }

      const techName = safeString(
        r?.assignedTechName || getTechName(r?.assignedTechId)
      ).trim();
      if (techFilter && techName !== techFilter) {
        return false;
      }

      if (fromDate || toDate) {
        const compareISO =
          r?.timestamps?.createdAt || r?.createdAt || r?.completedAtISO || "";
        if (!matchesDateRange(compareISO, fromDate, toDate)) {
          return false;
        }
      }

      return true;
    });
  }, [
    ros,
    query,
    statusFilter,
    advisorFilter,
    techFilter,
    fromDate,
    toDate,
    hasActiveFilters,
    getTechName,
  ]);

  const selectedRO = useMemo(() => {
    if (!selectedROId) return null;
    return ros.find((r) => String(r?.id) === String(selectedROId)) || null;
  }, [ros, selectedROId]);

  useEffect(() => {
    if (results.length === 1 && results[0]?.id) {
      setSelectedROId((prev) => (String(prev) === String(results[0].id) ? prev : results[0].id));
      return;
    }

    if (
      selectedROId &&
      !results.some((r) => String(r?.id) === String(selectedROId))
    ) {
      setSelectedROId(null);
      setIsModalOpen(false);
      setIsEditMode(false);
      setShowVoidConfirm(false);
    }
  }, [results, selectedROId]);

  const roHints = useMemo(() => {
    const q = normalize(query);
    if (!q) return [];
    const matches = ros
      .filter(
        (r) =>
          normalize(r?.roNumber).includes(q) || normalize(r?.tagNumber).includes(q)
      )
      .slice(0, 5)
      .map((r) => r?.roNumber)
      .filter(Boolean);
    return uniq(matches);
  }, [query, ros]);

  const selectedVM = useMemo(() => {
    if (!selectedRO) return null;
    return getRoRowViewModel(selectedRO, { getTechName });
  }, [selectedRO, getTechName]);

  const clearAll = useCallback(() => {
    setQuery("");
    setStatusFilter("");
    setAdvisorFilter("");
    setTechFilter("");
    setFromDate("");
    setToDate("");
    setSelectedROId(null);
    setIsModalOpen(false);
    setIsEditMode(false);
    setShowVoidConfirm(false);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const openModalForRo = useCallback((roId) => {
    setSelectedROId(roId || null);
    setIsModalOpen(true);
    setIsEditMode(false);
    setShowVoidConfirm(false);
    setModalBanner(null);
    hydratedModalRef.current = "";
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setShowVoidConfirm(false);
    setModalBanner(null);
    hydratedModalRef.current = "";
  }, []);

  useEffect(() => {
    if (!selectedRO || !isModalOpen) return;

    const hydrateKey = `${selectedROId}:${isEditMode ? "edit" : "view"}`;
    if (hydratedModalRef.current === hydrateKey) return;

    setEditForm(makeEditForm(selectedRO));
    setFieldErrors({
      roNumber: "",
      tagNumber: "",
      vinLast8: "",
      customerName: "",
      vehicle: "",
      advisorName: "",
    });
    hydratedModalRef.current = hydrateKey;
  }, [selectedROId, selectedRO, isModalOpen, isEditMode]);

  const validateRoNumber = useCallback(
    (value) => {
      const rn = safeString(value).trim();
      if (!rn) return "RO number is required.";

      const rnNorm = toUpperTrim(rn);
      const exists = ros.some(
        (r) =>
          String(r?.id) !== String(selectedRO?.id) &&
          toUpperTrim(r?.roNumber) === rnNorm
      );
      if (exists) return `RO #${rn} already exists in Flowtrak.`;
      return "";
    },
    [ros, selectedRO?.id]
  );

  const validateTagNumber = useCallback(
    (value) => {
      const tag = toUpperTrim(value);
      if (!tag) return "Tag # is required.";

      if (typeof store?.validateTagNumber === "function") {
        const res = store.validateTagNumber(tag, selectedRO?.id || "");
        if (!res?.ok) {
          return res?.message || "Tag # is already in use on an OPEN RO.";
        }
        return "";
      }

      const exists = ros.some(
        (r) =>
          String(r?.id) !== String(selectedRO?.id) &&
          toUpperTrim(r?.tagNumber) === tag
      );
      if (exists) return "Tag # is already in use on an OPEN RO.";
      return "";
    },
    [store, ros, selectedRO?.id]
  );

  const validateVin = useCallback((value) => {
    const vin = normalizeVinLast8(value);
    if (!vin) return "VIN (Last 8) is required.";
    if (vin.length !== 8) return "VIN (Last 8) must be exactly 8 characters.";
    return "";
  }, []);

  const validateCustomer = useCallback((value) => {
    return safeString(value).trim() ? "" : "Customer name is required.";
  }, []);

  const validateVehicle = useCallback((value) => {
    return safeString(value).trim() ? "" : "Vehicle is required.";
  }, []);

  const validateAdvisor = useCallback((value) => {
    return safeString(value).trim() ? "" : "Advisor name is required.";
  }, []);

  const runFullValidation = useCallback(() => {
    const nextErrors = {
      roNumber: validateRoNumber(editForm.roNumber),
      tagNumber: validateTagNumber(editForm.tagNumber),
      vinLast8: validateVin(editForm.vinLast8),
      customerName: validateCustomer(editForm.customerName),
      vehicle: validateVehicle(editForm.vehicle),
      advisorName: validateAdvisor(editForm.advisorName),
    };

    setFieldErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  }, [
    editForm,
    validateRoNumber,
    validateTagNumber,
    validateVin,
    validateCustomer,
    validateVehicle,
    validateAdvisor,
  ]);

  const onStartEdit = useCallback(() => {
    if (!selectedRO) return;
    setIsEditMode(true);
    setModalBanner(null);
    setShowVoidConfirm(false);
    setEditForm(makeEditForm(selectedRO));
    setFieldErrors({
      roNumber: "",
      tagNumber: "",
      vinLast8: "",
      customerName: "",
      vehicle: "",
      advisorName: "",
    });
    hydratedModalRef.current = `${selectedROId}:edit`;
  }, [selectedRO, selectedROId]);

  const onCancelEdit = useCallback(() => {
    if (!selectedRO) return;
    setIsEditMode(false);
    setShowVoidConfirm(false);
    setModalBanner(null);
    setEditForm(makeEditForm(selectedRO));
    setFieldErrors({
      roNumber: "",
      tagNumber: "",
      vinLast8: "",
      customerName: "",
      vehicle: "",
      advisorName: "",
    });
    hydratedModalRef.current = `${selectedROId}:view`;
  }, [selectedRO, selectedROId]);

  const onSaveEdit = useCallback(async () => {
    if (!selectedRO || typeof updateRO !== "function") return;
    if (!runFullValidation()) return;

    try {
      setIsSaving(true);
      setModalBanner(null);

      const normalizedVin = normalizeVinLast8(editForm.vinLast8);

      await updateRO(
        selectedRO.id,
        {
          roNumber: safeString(editForm.roNumber).trim(),
          tagNumber: toUpperTrim(editForm.tagNumber),
          vin: normalizedVin,
          vinLast8: normalizedVin,
          vin_last_8: normalizedVin,
          vin_last8: normalizedVin,
          vinLast: normalizedVin,
          customerName: safeString(editForm.customerName).trim(),
          vehicle: safeString(editForm.vehicle).trim(),
          advisorName: safeString(editForm.advisorName).trim(),
          advisorEmploymentId: safeString(editForm.advisorEmploymentId).trim(),
          promiseTimeISO: editForm.promiseTimeISO
            ? new Date(editForm.promiseTimeISO).toISOString()
            : "",
          workMix: safeString(editForm.workMix).trim() || WORK_MIX.OTHER,
          transportType:
            safeString(editForm.transportType).trim() || TRANSPORT_TYPES.OTHER,
          concern:
            safeString(editForm.workMix).trim() === WORK_MIX.MAINTENANCE
              ? "Maintenance"
              : safeString(editForm.workMix).trim() === WORK_MIX.DIAGNOSIS
              ? "Diagnosis"
              : "Other",
        },
        "RO_EDITED",
        {
          byRole: safeString(currentUser?.role || "user"),
          byName: safeString(currentUser?.name || "User"),
        }
      );

      await refreshRos();

      setIsEditMode(false);
      setModalBanner({
        type: "success",
        message: `RO #${safeString(editForm.roNumber).trim()} updated.`,
      });
      hydratedModalRef.current = `${selectedROId}:view`;
    } catch (err) {
      setModalBanner({
        type: "error",
        message: String(err?.message || err),
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedRO,
    updateRO,
    runFullValidation,
    editForm,
    currentUser,
    selectedROId,
    refreshRos,
  ]);

  const onClickVoid = useCallback(() => {
    if (!canVoidRole(currentUser?.role)) {
      setModalBanner({
        type: "error",
        message: "You do not have permission to void this RO.",
      });
      return;
    }
    setShowVoidConfirm(true);
  }, [currentUser?.role]);

  const onConfirmVoid = useCallback(async () => {
    if (!selectedRO || typeof deleteRO !== "function") return;

    try {
      setIsVoiding(true);
      await deleteRO(selectedRO.id);
      await refreshRos();
      setShowVoidConfirm(false);
      setIsEditMode(false);
      setIsModalOpen(false);
      setSelectedROId(null);
      hydratedModalRef.current = "";
    } catch (err) {
      setModalBanner({
        type: "error",
        message: String(err?.message || err),
      });
    } finally {
      setIsVoiding(false);
    }
  }, [selectedRO, deleteRO, refreshRos]);

  const renderFieldOrInput = (label, value, fieldKey, inputNode, helper = "") => {
    const error = fieldErrors[fieldKey] || "";
    return (
      <div style={detailFieldCardStyle()}>
        <div style={detailLabelStyle()}>{label}</div>
        {isEditMode && inputNode ? (
          <>
            {inputNode}
            {error ? (
              <div style={errorTextStyle()}>{error}</div>
            ) : helper ? (
              <div style={helperTextStyle()}>{helper}</div>
            ) : null}
          </>
        ) : (
          <div style={detailValueStyle()}>{value || "—"}</div>
        )}
      </div>
    );
  };

  return (
    <div style={pageShell}>
      <div style={pageHeaderRow}>
        <div>
          <div style={pageEyebrow}>Tools</div>
          <h1 style={pageTitle}>RO Search</h1>
          <div style={pageSubtitle}>
            Find repair orders by RO, tag, customer, vehicle, VIN, status, or notes.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <span style={resultCountPill}>
            Results: {results.length}
          </span>
        </div>
      </div>

      <div style={sectionCardStyle()}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 1fr) auto auto",
            gap: 8,
            alignItems: "center",
          }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search RO #, tag, customer, vehicle, VIN, notes..."
            style={fieldStyle()}
          />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            style={searchButton}
          >
            {showAdvanced ? "Hide Filters" : "Advanced Filters"}
          </button>

          <button type="button" onClick={clearAll} style={searchButton}>
            Clear
          </button>
        </div>

        <div
          style={{
            marginTop: 8,
            fontSize: 11.5,
            color: "rgba(15,23,42,0.58)",
            lineHeight: 1.3,
          }}
        >
          Search specific repair orders without showing a broad preview list by default.
        </div>

        {roHints.length > 0 ? (
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              fontSize: 11.5,
              color: "rgba(15,23,42,0.58)",
            }}
          >
            <span style={{ fontWeight: 800 }}>Quick matches:</span>
            {roHints.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setQuery(String(h))}
                style={{
                  ...resultCountPill,
                  cursor: "pointer",
                  background: "#ffffff",
                  color: "rgba(15,23,42,0.80)",
                }}
                title="Click to search this RO"
              >
                {h}
              </button>
            ))}
          </div>
        ) : null}

        {showAdvanced ? (
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 8,
            }}
          >
            <input
              type="text"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={fieldStyle()}
              placeholder="Filter by status"
            />

            <select
              value={advisorFilter}
              onChange={(e) => setAdvisorFilter(e.target.value)}
              style={fieldStyle()}
            >
              <option value="">All Advisors</option>
              {advisorOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={techFilter}
              onChange={(e) => setTechFilter(e.target.value)}
              style={fieldStyle()}
              placeholder="Filter by technician"
            />

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={fieldStyle()}
              max={toDate || undefined}
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={fieldStyle()}
              min={fromDate || undefined}
            />
          </div>
        ) : null}
      </div>

      {!hasActiveFilters ? (
        <div style={sectionCardStyle()}>
          <div style={{ ...sectionTitleText, marginBottom: 7 }}>Search Ready</div>
          <div style={emptyStateStyle()}>
            Enter an RO number, tag, customer, vehicle, VIN, or note keyword to open a
            focused search result.
          </div>
        </div>
      ) : (
        <div
          className="ro-search-results-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(420px, 1fr) minmax(360px, 0.78fr)",
            gap: 10,
            alignItems: "start",
          }}
        >
          <div style={sectionCardStyle()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div style={sectionTitleText}>Matches</div>
              <span style={resultCountPill}>{results.length}</span>
            </div>

            {results.length === 0 ? (
              <div style={emptyStateStyle()}>
                No repair orders match this search.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {results.slice(0, 80).map((ro) => {
                  const active = String(ro?.id) === String(selectedROId);
                  const createdISO = ro?.timestamps?.createdAt || ro?.createdAt;
                  const tag = safeString(ro?.tagNumber).trim();
                  const vm = getRoRowViewModel(ro, { getTechName });
                  const summaryTech = vm.technician;
                  const lastNote = getLatestNotePreview(ro);
                  const corePills = getRoCorePills(ro);
                  const indicators = getRoSecondaryIndicators(ro);
                  const roNumberStyle = vm.highlightRoNumber
                    ? { color: "#b91c1c" }
                    : { color: "rgba(15,23,42,0.94)" };

                  return (
                    <button
                      key={ro?.id || ro?.roNumber}
                      type="button"
                      onClick={() => openModalForRo(ro?.id || null)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: active
                          ? "1px solid #bfdbfe"
                          : "1px solid rgba(15,23,42,0.08)",
                        borderRadius: 12,
                        padding: 8,
                        background: active ? "#f8fbff" : "#ffffff",
                        boxShadow: active
                          ? "0 0 0 2px rgba(59,130,246,0.08)"
                          : "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ minWidth: 0, flex: "1 1 260px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 900,
                                fontSize: 13.25,
                                ...roNumberStyle,
                              }}
                              title="Open RO details"
                            >
                              {vm.roLabel}
                            </span>

                            {tag ? <span style={tagPillStyle()}>Tag #{tag}</span> : null}
                          </div>

                          <div
                            style={{
                              fontSize: 11.5,
                              color: "rgba(15,23,42,0.74)",
                              marginTop: 3,
                            }}
                          >
                            <b>{vm.customer}</b> — {vm.vehicle}
                          </div>

                          <div
                            style={{
                              fontSize: 10.8,
                              color: "rgba(15,23,42,0.55)",
                              marginTop: 2,
                            }}
                          >
                            Created: {formatWhen(createdISO)} • Tech: <b>{summaryTech}</b>
                          </div>

                          <div
                            style={{
                              fontSize: 10.8,
                              color: "rgba(15,23,42,0.55)",
                              marginTop: 2,
                            }}
                          >
                            Status: <b>{statusLabel(ro)}</b>
                          </div>
                        </div>

                        {indicators.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 5,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                              maxWidth: 220,
                            }}
                          >
                            {indicators.map((item) => (
                              <span
                                key={`${item.type}-${item.key}`}
                                style={secondaryIndicatorStyle(item.type)}
                              >
                                {item.label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>

                      {corePills.length > 0 ? (
                        <div
                          style={{
                            marginTop: 7,
                            display: "flex",
                            gap: 5,
                            flexWrap: "wrap",
                          }}
                        >
                          {corePills.map((pill) => (
                            <span
                              key={`${pill.type}-${pill.key}`}
                              style={corePillStyle(pill.type)}
                            >
                              {pill.label}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {lastNote ? (
                        <div
                          style={{
                            marginTop: 7,
                            fontSize: 10.8,
                            color: "rgba(15,23,42,0.55)",
                            display: "flex",
                            gap: 5,
                          }}
                        >
                          <span style={{ fontWeight: 850 }}>Last note:</span>
                          <span>{lastNote}</span>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={sectionCardStyle()}>
            <div style={{ ...sectionTitleText, marginBottom: 8 }}>Details</div>

            {!selectedRO || !selectedVM ? (
              <div style={emptyStateStyle()}>
                Click any RO row on the left to open details.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: 7,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openModalForRo(selectedRO?.id || null)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      margin: 0,
                      cursor: "pointer",
                      fontWeight: 900,
                      fontSize: 13.25,
                      color: shouldHighlightRoNumber(selectedRO)
                        ? "#b91c1c"
                        : "rgba(15,23,42,0.94)",
                      fontFamily: "inherit",
                    }}
                  >
                    {selectedVM.roLabel}
                  </button>

                  {selectedVM.tagNumber ? (
                    <span style={tagPillStyle()}>Tag #{selectedVM.tagNumber}</span>
                  ) : null}
                </div>

                <div
                  style={{
                    marginTop: 3,
                    fontSize: 11.5,
                    color: "rgba(15,23,42,0.74)",
                  }}
                >
                  <b>{selectedVM.customer}</b> — {formatVehicleLabel(selectedRO)}
                </div>

                <div
                  style={{
                    marginTop: 7,
                    display: "flex",
                    gap: 5,
                    flexWrap: "wrap",
                  }}
                >
                  {selectedVM.corePills.map((pill) => (
                    <span
                      key={`${pill.type}-${pill.key}`}
                      style={corePillStyle(pill.type)}
                    >
                      {pill.label}
                    </span>
                  ))}

                  {selectedVM.secondaryIndicators.map((item) => (
                    <span
                      key={`${item.type}-${item.key}`}
                      style={secondaryIndicatorStyle(item.type)}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 10,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 6,
                    fontSize: 11.5,
                    color: "rgba(15,23,42,0.58)",
                  }}
                >
                  <div>
                    Advisor: <b>{selectedVM.advisor}</b>
                  </div>
                  <div>
                    Tech: <b>{selectedVM.technician}</b>
                  </div>
                  <div>
                    Flagged Hours: <b>{formatFlaggedHours(selectedRO)}</b>
                  </div>
                  <div>
                    Created: <b>{formatWhen(selectedRO.timestamps?.createdAt || selectedRO.createdAt)}</b>
                  </div>
                  <div>
                    Promise: <b>{formatWhen(selectedRO.promiseTimeISO)}</b>
                  </div>
                  <div>
                    Parts: <b>{safeString(selectedRO.partsStatus || "NONE").toUpperCase() || "—"}</b>
                  </div>
                  <div>
                    Lifecycle: <b>{lifecycleLabel(selectedRO)}</b>
                  </div>
                  <div>
                    Status: <b>{statusLabel(selectedRO)}</b>
                  </div>
                  <div>
                    VIN: <b>{getVinDisplay(selectedRO) || "—"}</b>
                  </div>
                </div>

                {selectedVM.latestNote ? (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 8,
                      borderRadius: 12,
                      border: "1px solid rgba(15,23,42,0.07)",
                      background: "#f8fafc",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10.2,
                        fontWeight: 850,
                        color: "rgba(15,23,42,0.44)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: 3,
                      }}
                    >
                      Last Note
                    </div>
                    <div style={{ fontSize: 11.5, color: "rgba(15,23,42,0.68)" }}>
                      {selectedVM.latestNote}
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: 10 }}>
                  <ROStatusTimeline
                    ro={selectedRO}
                    getTechName={getTechName}
                    title="History Timeline"
                    emptyMessage="No history available."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>
        {`
          @media (max-width: 1120px) {
            .ro-search-results-grid {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 720px) {
            div[style*="grid-template-columns: minmax(280px"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      {isModalOpen && selectedRO ? (
        <div style={modalOverlayStyle()} onClick={closeModal}>
          <div
            style={modalCardStyle(showVoidConfirm ? 520 : 920)}
            onClick={(e) => e.stopPropagation()}
          >
            {showVoidConfirm ? (
              <>
                <div style={modalHeaderStyle()}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
                      Void RO #{safeString(selectedRO?.roNumber || "—")}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12.5, color: "#6b7280" }}>
                      This will void the RO and remove it from active workflow, while preserving the record for history and audit. Use this for canceled or bad-entry ROs.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowVoidConfirm(false)}
                    style={{
                      marginLeft: "auto",
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={modalBodyStyle()}>
                  <div
                    style={{
                      border: "1px solid #fee2e2",
                      background: "#fff7f7",
                      color: "#991b1b",
                      borderRadius: 12,
                      padding: 12,
                      fontSize: 13,
                      lineHeight: 1.45,
                    }}
                  >
                    Are you sure you want to void this RO?
                    <div style={{ marginTop: 8, color: "#374151" }}>
                      <b>RO:</b> {safeString(selectedRO?.roNumber || "—")}
                    </div>
                    <div style={{ color: "#374151" }}>
                      <b>Customer:</b> {safeString(selectedRO?.customerName || "—")}
                    </div>
                    <div style={{ color: "#374151" }}>
                      <b>Vehicle:</b> {safeString(selectedRO?.vehicle || "—")}
                    </div>
                  </div>
                </div>

                <div style={modalFooterStyle()}>
                  <button
                    type="button"
                    onClick={() => setShowVoidConfirm(false)}
                    style={btnStyle("default", isVoiding)}
                    disabled={isVoiding}
                  >
                    Go Back
                  </button>

                  <button
                    type="button"
                    onClick={onConfirmVoid}
                    style={btnStyle("danger", isVoiding)}
                    disabled={isVoiding}
                  >
                    {isVoiding ? "Voiding…" : "Confirm Void"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={modalHeaderStyle()}>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
                        RO #{safeString(selectedRO?.roNumber || "—")}
                      </div>

                      {safeString(selectedRO?.tagNumber).trim() ? (
                        <span style={tagPillStyle()}>
                          Tag #{safeString(selectedRO?.tagNumber).trim()}
                        </span>
                      ) : null}
                    </div>

                    <div style={{ marginTop: 4, fontSize: 12.5, color: "#6b7280" }}>
                      {safeString(selectedRO?.customerName || "—")} •{" "}
                      {safeString(selectedRO?.vehicle || "—")}
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      {selectedVM?.corePills?.map((pill) => (
                        <span
                          key={`${pill.type}-${pill.key}`}
                          style={corePillStyle(pill.type)}
                        >
                          {pill.label}
                        </span>
                      ))}

                      {selectedVM?.secondaryIndicators?.map((item) => (
                        <span
                          key={`${item.type}-${item.key}`}
                          style={secondaryIndicatorStyle(item.type)}
                        >
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      marginLeft: "auto",
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={modalBodyStyle()}>
                  {modalBanner ? (
                    <div
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        fontWeight: 900,
                        fontSize: 12.5,
                        background:
                          modalBanner.type === "success" ? "#ecfdf5" : "#fff1f2",
                        color:
                          modalBanner.type === "success" ? "#065f46" : "#991b1b",
                      }}
                    >
                      {modalBanner.message}
                    </div>
                  ) : null}

                  <div style={detailGridStyle()}>
                    {renderFieldOrInput(
                      "RO Number",
                      safeString(selectedRO?.roNumber).trim(),
                      "roNumber",
                      <input
                        value={editForm.roNumber}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, roNumber: e.target.value }))
                        }
                        style={fieldStyle()}
                        placeholder="RO number"
                      />
                    )}

                    {renderFieldOrInput(
                      "Tag Number",
                      safeString(selectedRO?.tagNumber).trim(),
                      "tagNumber",
                      <input
                        value={editForm.tagNumber}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, tagNumber: e.target.value }))
                        }
                        style={fieldStyle()}
                        placeholder="Tag #"
                      />
                    )}

                    {renderFieldOrInput(
                      "VIN (Last 8)",
                      getVinDisplay(selectedRO),
                      "vinLast8",
                      <input
                        value={editForm.vinLast8}
                        onChange={(e) =>
                          setEditForm((m) => ({
                            ...m,
                            vinLast8: normalizeVinLast8(e.target.value),
                          }))
                        }
                        style={{ ...fieldStyle(), textTransform: "uppercase" }}
                        placeholder="VIN Last 8"
                        maxLength={8}
                      />,
                      "Exactly 8 letters/numbers."
                    )}

                    {renderFieldOrInput(
                      "Advisor",
                      safeString(selectedRO?.advisorName).trim(),
                      "advisorName",
                      <select
                        value={advisorEditSelectValue}
                        onChange={(e) => applyAdvisorEditSelection(e.target.value)}
                        style={fieldStyle()}
                      >
                        <option value="">Select advisor…</option>
                        {advisorEditOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {renderFieldOrInput(
                      "Customer",
                      safeString(selectedRO?.customerName).trim(),
                      "customerName",
                      <input
                        value={editForm.customerName}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, customerName: e.target.value }))
                        }
                        style={fieldStyle()}
                        placeholder="Customer name"
                      />
                    )}

                    {renderFieldOrInput(
                      "Vehicle",
                      safeString(selectedRO?.vehicle).trim(),
                      "vehicle",
                      <input
                        value={editForm.vehicle}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, vehicle: e.target.value }))
                        }
                        style={fieldStyle()}
                        placeholder="Vehicle"
                      />
                    )}

                    {renderFieldOrInput(
                      "Promise Time",
                      formatWhen(selectedRO?.promiseTimeISO),
                      "promiseTimeISO",
                      <input
                        type="datetime-local"
                        step={900}
                        value={editForm.promiseTimeISO}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, promiseTimeISO: e.target.value }))
                        }
                        style={fieldStyle()}
                      />
                    )}

                    {renderFieldOrInput(
                      "Assigned Tech",
                      safeString(
                        selectedRO?.assignedTechName ||
                          getTechName(selectedRO?.assignedTechId)
                      ).trim(),
                      "assignedTech",
                      null
                    )}

                    {renderFieldOrInput(
                      "Flagged Hours",
                      formatFlaggedHours(selectedRO),
                      "flaggedHours",
                      null
                    )}

                    {renderFieldOrInput(
                      "Work Mix",
                      safeString(selectedRO?.workMix).trim() || "—",
                      "workMix",
                      <select
                        value={editForm.workMix}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, workMix: e.target.value }))
                        }
                        style={fieldStyle()}
                      >
                        <option value={WORK_MIX.MAINTENANCE}>Maintenance</option>
                        <option value={WORK_MIX.DIAGNOSIS}>Diagnosis</option>
                        <option value={WORK_MIX.OTHER}>Other</option>
                      </select>
                    )}

                    {renderFieldOrInput(
                      "Transport Type",
                      safeString(selectedRO?.transportType).trim() || "—",
                      "transportType",
                      <select
                        value={editForm.transportType}
                        onChange={(e) =>
                          setEditForm((m) => ({ ...m, transportType: e.target.value }))
                        }
                        style={fieldStyle()}
                      >
                        <option value={TRANSPORT_TYPES.WAITER}>Waiter</option>
                        <option value={TRANSPORT_TYPES.LOANER}>Loaner</option>
                        <option value={TRANSPORT_TYPES.SHUTTLE}>Shuttle</option>
                        <option value={TRANSPORT_TYPES.OTHER}>Other</option>
                      </select>
                    )}

                    {renderFieldOrInput(
                      "Status",
                      statusLabel(selectedRO),
                      "status",
                      null
                    )}

                    {renderFieldOrInput(
                      "Lifecycle",
                      lifecycleLabel(selectedRO),
                      "lifecycle",
                      null
                    )}
                  </div>

                  {selectedVM?.latestNote ? (
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fafafa",
                      }}
                    >
                      <div style={detailLabelStyle()}>Last Note</div>
                      <div style={{ fontSize: 13, color: "#374151" }}>
                        {selectedVM.latestNote}
                      </div>
                    </div>
                  ) : null}

                  <div>
                    <ROStatusTimeline
                      ro={selectedRO}
                      getTechName={getTechName}
                      title="History Timeline"
                      emptyMessage="No history available."
                    />
                  </div>
                </div>

                <div style={modalFooterStyle()}>
                  {isEditMode ? (
                    <>
                      <button
                        type="button"
                        onClick={onCancelEdit}
                        style={btnStyle("default", isSaving)}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={onClickVoid}
                        style={btnStyle("danger", isSaving)}
                        disabled={isSaving}
                      >
                        Void RO
                      </button>

                      <button
                        type="button"
                        onClick={onSaveEdit}
                        style={btnStyle("primary", isSaving)}
                        disabled={isSaving}
                      >
                        {isSaving ? "Saving…" : "Save"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={closeModal} style={btnStyle()}>
                        Close
                      </button>

                      <button
                        type="button"
                        onClick={onClickVoid}
                        style={btnStyle("danger")}
                      >
                        Void RO
                      </button>

                      <button
                        type="button"
                        onClick={onStartEdit}
                        style={btnStyle("primary")}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
